import { Navigate, Outlet, useLocation } from 'react-router';
import { Spin, Typography } from 'antd';
import {
  isSectionOpen,
  PORTAL_SECTIONS,
  startSection,
  type Permission,
  type PortalSectionId,
  type SectionAccess,
} from '@technic/contracts';
import { useAuth, useAuthReconnecting } from '@entities/session';
// Экран «разделов нет» — из `pages`: стартовая страница его рисует, а не уводит на него, и своего
// адреса у него нет вовсе.
import { NoSectionsPage } from '@pages/no-sections';

function FullScreenSpin({ note }: { note?: string }) {
  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        textAlign: 'center',
      }}
    >
      <Spin size="large" />
      {note && <Typography.Text type="secondary">{note}</Typography.Text>}
    </div>
  );
}

export function ProtectedRoute() {
  const { status, user } = useAuth();
  const reconnecting = useAuthReconnecting();
  const location = useLocation();

  // While the server cannot answer, the tab keeps retrying instead of sending the person to sign
  // in: the session is intact, and signing in would hit the same unavailable server (ADR 0233).
  if (status === 'loading') {
    return (
      <FullScreenSpin
        note={reconnecting ? 'Нет связи с сервером. Повторяем подключение…' : undefined}
      />
    );
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Единственный законный редирект на смену пароля: пароль действительно требуют сменить. Всё
  // остальное, что приводило человека на эту форму, было промахом стартовой страницы.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

/**
 * Доступ учётки в том виде, в каком его спрашивает реестр разделов: роль и `canUse`.
 *
 * Одним способом на все вопросы портала про разделы — «что показать в меню», «пускать ли по
 * адресу», «куда приземлить после входа». До реестра каждый спрашивал по-своему, и промах одной
 * копии выходил наружу только у той роли, для которой пропущенный раздел единственный: так
 * стартовая страница потеряла «Орг.технику» и отправляла сервисную компанию на смену пароля.
 *
 * `canUse`, а не `can`: у роли отдела без площадки право на вывоз есть, а работать им не над чем
 * (ADR 0062) — раздел с пустой областью не должен ни стоять в меню, ни открываться по ссылке.
 */
function useSectionAccess(): SectionAccess {
  const { user, canUse } = useAuth();
  return { role: user?.role ?? null, canUse };
}

/**
 * Стартовая страница: первый открытый учётке раздел — или экран «разделов нет».
 *
 * Фиксированной страницы «по умолчанию» у портала нет: «Вывоз мусора» закрыт руководителю
 * строительства (ADR 0025), «Заказ ТС» — оператору вывоза (ADR 0010), и любая жёсткая ссылка
 * отправляла бы половину ролей туда, откуда их тут же выкидывает обратно. Порядок разделов держит
 * реестр (`startOrder`), и он намеренно не совпадает с порядком меню.
 *
 * Пустой ответ реестра рисуется **экраном, а не редиректом**: раньше перебор заканчивался адресом
 * `/change-password`, и «доступ не настроен» приезжало к человеку формой смены пароля — проблема
 * доступа выглядела как просроченный пароль, а сменить его не помогало. Экран стоит
 * index-маршрутом внутри каркаса, поэтому вокруг остаются меню учётной записи и выход.
 */
export function HomeRedirect() {
  const access = useSectionAccess();
  const section = startSection(access);
  return section ? <Navigate to={section.path} replace /> : <NoSectionsPage />;
}

/**
 * Гейт раздела: пускает по тому же `isSectionOpen`, которым раздел попадает в меню и становится
 * стартовым. Одно условие на три вопроса — в этом вся правка: гейты маршрутов, меню и стартовая
 * страница жили порознь и разъезжались на каждом новом разделе.
 *
 * Кабинет водителя (ADR 0102) закрывается этим же гейтом, хотя условие у него особое — роль вместе
 * с правом. Особенность переехала в поле `roles` реестра и объясняется там: `driverCabinet.read`
 * есть у администратора, потому что права у него все, и по одному праву кабинет открывался ему и
 * стартовой страницей, и по прямой ссылке — а выйти из второго контура нечем, у него ни меню, ни
 * разделов.
 *
 * Отказ ведёт на `/`, а не на посчитанный здесь раздел: адрес отказа один, и решает по нему
 * стартовая страница. Зацикливания это не даёт по построению — гейт и стартовая спрашивают один
 * предикат, и `startSection` не может вернуть раздел, из которого выкидывает. Держится это на том,
 * что сам `/` не накрыт гейтом: надень его на корень — и отказ станет отбивать входящего в себя
 * же, пустым экраном и без диагноза.
 */
export function RequireSection({ id }: { id: PortalSectionId }) {
  const access = useSectionAccess();
  // Раздела с таким `id` в реестре не может не быть — это тот же тип, которым описаны его строки;
  // `find` возвращает `undefined` только по сигнатуре, и отвечать на это отдельной дверью незачем.
  const section = PORTAL_SECTIONS.find((s) => s.id === id);
  if (!section || !isSectionOpen(section, access)) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Гейт **подстраницы** со своим правом (`/vehicle-requests/weekly/:id`). Разделом она не является:
 * пункта меню у неё нет и стартовой она быть не может, поэтому и строки в реестре ей не положено.
 *
 * Право вместе с областью, а не список ролей: список пришлось бы держать в согласии с проверками
 * API вручную, а право одно и то же по обе стороны (ADR 0021, ADR 0062).
 */
export function RequirePermission({ permission }: { permission: Permission | Permission[] }) {
  const { canUse } = useAuth();
  // Список прав — для страницы из нескольких вкладок: администрирование открывается и тому, кто
  // ведёт учётки, и тому, кто настраивает рассылки. Права разные, страница одна, и требовать оба
  // значило бы закрыть её каждому, у кого есть только одно.
  const required = Array.isArray(permission) ? permission : [permission];
  // Отказ туда же, куда у `RequireSection`: про разделы учётки знает стартовая страница, а гейту
  // подстраницы знать их незачем.
  if (!required.some((p) => canUse(p))) return <Navigate to="/" replace />;
  return <Outlet />;
}
